import { create } from 'zustand';

/**
 * Transport health of the signed-in session, distinct from `navigator.onLine`.
 *
 * `degraded` flips on when a silent token refresh fails for a reason that is
 * NOT a definitive auth failure — network error, 429 from a shared rate-limit
 * bucket, 5xx during an API deploy. The session is kept and the refresh is
 * retried with backoff (see lib/api.ts); OfflineBanner uses this flag to tell
 * the user why things are stalling instead of silently bouncing them to
 * /login as the old interceptor did.
 *
 * `retriesExhausted` flips on once the backoff schedule has used up every
 * attempt. The session is still kept, but nothing is retrying any more until
 * the browser reports `online` (lib/api.ts listens for it and restarts the
 * schedule), so the banner must stop promising a background reconnect.
 */
interface SessionHealthState {
  degraded: boolean;
  retriesExhausted: boolean;
  setDegraded: (degraded: boolean) => void;
  setRetriesExhausted: (retriesExhausted: boolean) => void;
}

export const useSessionHealthStore = create<SessionHealthState>((set) => ({
  degraded: false,
  retriesExhausted: false,
  setDegraded: (degraded) => set(degraded ? { degraded } : { degraded, retriesExhausted: false }),
  setRetriesExhausted: (retriesExhausted) => set({ retriesExhausted }),
}));
